import axios from 'axios'
import request from './index'
import { getPharmaAiToken, redirectToSso } from '@/utils/auth'

const adminHttp = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '/api',
  timeout: 15000,
  headers: { 'Content-Type': 'application/json;charset=UTF-8' },
  withCredentials: true,
})

adminHttp.interceptors.request.use((config) => {
  const token = getPharmaAiToken()
  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

adminHttp.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 403) {
      redirectToSso()
    }
    return Promise.reject(error)
  },
)

/** 采集配置列表 GET /v1/admin/sources/getDataSourceList */
export function getDataSourceList() {
  return adminHttp.get('/v1/admin/sources/getDataSourceList').then((res) => res.data)
}

/**
 * 频道配置详情 GET /v1/admin/sources/{source_id}/getDataSourceDetail
 * 404 时返回 null（表示尚未配置），其它错误抛出
 */
export async function getDataSourceDetail(sourceId) {
  const id = String(sourceId || '').trim()
  if (!id) {
    throw new Error('sourceId不能为空')
  }
  try {
    const res = await adminHttp.get(
      `/v1/admin/sources/${encodeURIComponent(id)}/getDataSourceDetail`,
    )
    const body = res.data
    if (!body || typeof body !== 'object') return null
    // 业务失败：{ status: false, data: null }
    if (body.status === false && !body.config) return null
    // 标准包装：{ status: true, data: { datasource_id, config } }
    if (body.data && typeof body.data === 'object') return body.data
    // 直连：{ datasource_id, config }
    if (body.config || body.datasource_id) return body
    return null
  } catch (err) {
    if (err?.response?.status === 404) {
      return null
    }
    throw err
  }
}

/** 保存爬虫参数 POST /v1/admin/sources/saveCrawlParams */
export function saveCrawlParams(payload) {
  return adminHttp.post('/v1/admin/sources/saveCrawlParams', payload).then((res) => res.data)
}

/** @deprecated 旧接口别名 */
export function getSourceConfig(sourceId) {
  return getDataSourceDetail(sourceId)
}

/** openFDA 导入历史（兼容旧字段） */
export function getOpenFdaImportHistory(params = {}) {
  return request({
    url: '/v1/admin/openfda/import-history',
    method: 'get',
    params,
  })
}

/**
 * openFDA 上传文件并创建异步导入任务
 * POST /v1/admin/openfda/import  multipart field: file
 * 成功仅表示任务已创建，需用 task_no 轮询详情
 */
export async function importOpenFdaFile(file, { onUploadProgress } = {}) {
  const formData = new FormData()
  formData.append('file', file)

  try {
    const res = await adminHttp.post('/v1/admin/openfda/import', formData, {
      // 只覆盖上传传输阶段；后台导入通过轮询查询
      timeout: 2 * 60 * 60 * 1000,
      onUploadProgress,
      transformRequest: [(data, headers) => {
        if (headers && typeof headers === 'object') {
          delete headers['Content-Type']
        }
        return data
      }],
      validateStatus: (status) => status >= 200 && status < 300,
    })

    const body = res.data
    if (body && (body.errno !== 0 || body.status === false)) {
      const err = new Error(body.errmsg || '创建导入任务失败')
      err.errno = body.errno
      err.responseBody = body
      throw err
    }
    return body
  } catch (error) {
    const status = error?.response?.status
    if (status === 413) {
      const err = new Error(
        '文件过大，被网关拒绝（HTTP 413）。请运维调大 nginx client_max_body_size 后重试。',
      )
      err.status = 413
      throw err
    }
    if (error?.message && /413|Request Entity Too Large/i.test(String(error.response?.data || error.message))) {
      const err = new Error(
        '文件过大，被网关拒绝（HTTP 413）。请运维调大 nginx client_max_body_size 后重试。',
      )
      err.status = 413
      throw err
    }
    throw error
  }
}

/** 查询 openFDA 导入任务详情 GET /v1/admin/openfda/import-tasks/{task_no} */
export function getOpenFdaImportTask(taskNo) {
  return request({
    url: `/v1/admin/openfda/import-tasks/${encodeURIComponent(taskNo)}`,
    method: 'get',
    withCredentials: true,
  })
}

/** 查询 openFDA 导入任务列表 GET /v1/admin/openfda/import-tasks */
export function getOpenFdaImportTasks(params = {}) {
  return request({
    url: '/v1/admin/openfda/import-tasks',
    method: 'get',
    params,
    withCredentials: true,
  })
}

/**
 * 每日数据处理统计
 * GET /v1/admin/statistics/daily
 * @param {{ start_date: string, end_date: string, data_type: string }} params
 */
export function getDailyStatistics(params) {
  return request({
    url: '/v1/admin/statistics/daily',
    method: 'get',
    params,
    withCredentials: true,
  })
}
